import { useState, useEffect } from 'react';
import { User, Lock, Calendar, DollarSign, LogOut, AlertCircle, CheckCircle,TimerIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import useAuthStore from '../store/authStore';

export default function UserProfile() {
  const [activeTab, setActiveTab] = useState('profile');
  const [profile, setProfile] = useState(null);
  const [bookings, s$ookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
    fetchBookings();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const res = await api.get('/users/profile');
      setProfile(res.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      const res = await api.get('/users/bookings');
      s$ookings(res.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };
 const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setErrorMessage('Passwords do not match');
      return;
    }

    try {
      await api.post('/users/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      setSuccessMessage('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      setErrorMessage(error.response?.data?.message || 'Error changing password');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 py-12">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-6 border border-gray-200 dark:border-slate-800 sticky top-20">
              {/* Avatar */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-white" />
                </div>
              </div>

              {/* Profile Info */}
              <div className="text-center mb-6 pb-6 border-b border-gray-200 dark:border-slate-800">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">{profile?.name}</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{profile?.email}</p>
              </div>

              {/* Navigation */}
              <nav className="space-y-2 mb-6">
                {['profile', 'bookings', 'password'].map((tab) => {
                  const icons = { profile: User, bookings: Calendar, password: Lock };
                  const labels = { profile: 'My Profile', bookings: 'My Bookings', password: 'Change Password' };
                  const Icon = icons[tab];
                  return (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                        activeTab === tab
                          ? 'bg-green-500 text-white shadow-lg'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{labels[tab]}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Logout Button */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors font-medium"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="md:col-span-3 space-y-6">
            {/* Profile Tab */}
            {activeTab === 'profile' && profile && (
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-8 border border-gray-200 dark:border-slate-800">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Profile Information</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Full Name</label>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{profile.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{profile.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Account Type</label>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold capitalize bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-1 rounded-full w-fit">
                      {profile.role}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Member Since</label>
                    <p className="text-lg text-gray-900 dark:text-white font-semibold">{new Date(profile.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Bookings Tab */}
           {activeTab === 'bookings' && (
  <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-6 md:p-8 border border-gray-200 dark:border-slate-800">
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6 md:mb-8">
      My Bookings
    </h2>

    {loading ? (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    ) : bookings.length === 0 ? (
      <div className="text-center py-16">
        <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <p className="text-xl text-gray-600 dark:text-gray-400">You haven't made any bookings yet</p>
        <p className="text-gray-500 dark:text-gray-500 mt-2">
          Start exploring and book your first tour!
        </p>
      </div>
    ) : (
      <div className="space-y-4">
        {bookings.map((booking) => (
          <div
            key={booking.id}
            className="
              flex flex-col sm:flex-row gap-4 
              p-4 bg-gray-50 dark:bg-slate-800 rounded-lg 
              border border-gray-200 dark:border-slate-700 
              hover:shadow-md transition-shadow
            "
          >
            {/* Image */}
            <img
              src={`https://backend.ysskytravel.com/uploads/tours/${booking.image_url}` || '/placeholder.svg?height=120&width=120&query=tour'}
              alt={booking.title}
              className="w-full sm:w-24 h-40 sm:h-24 object-cover rounded-lg"
            />

            {/* Content */}
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                {booking.title}
              </h3>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-3">
                <span className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-sm">
                  <Calendar className="w-4 h-4" /> #{booking.id}
                </span>

                <span className="inline-flex items-center gap-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-sm">
                  <DollarSign className="w-4 h-4" />
                  ${parseFloat(booking.total_price || 0).toFixed(2)}
                </span>

                <span className="inline-flex items-center gap-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-3 py-1 rounded-full text-sm">
                  {booking.participants} {booking.participants === 1 ? 'Person' : 'People'}
                </span>
                <span className="inline-flex items-center gap-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-sm">
                  <TimerIcon className="w-4 h-4" /> Tour Date : {formatDate(booking.tour_date)}
                </span>
              </div>

              {/* Status + date */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    booking.status === 'confirmed'
                      ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                      : booking.status === 'pending'
                      ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                  }`}
                >
                  {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                </span>

                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Booked on {formatDate(booking.created_at)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    )}
  </div>
)}


            {/* Password Tab */}
            {activeTab === 'password' && (
              <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg p-8 border border-gray-200 dark:border-slate-800">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">Change Password</h2>

                {successMessage && (
                  <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <p className="text-green-600 dark:text-green-400">{successMessage}</p>
                  </div>
                )}

                {errorMessage && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-red-600 dark:text-red-400">{errorMessage}</p>
                  </div>
                )}

                <form onSubmit={handlePasswordChange} className="space-y-6 max-w-md">
                  {['currentPassword', 'newPassword', 'confirmPassword'].map((field) => {
                    const labels = {
                      currentPassword: 'Current Password',
                      newPassword: 'New Password',
                      confirmPassword: 'Confirm Password',
                    };
                    return (
                      <div key={field}>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{labels[field]}</label>
                        <input
                          type="password"
                          value={passwordForm[field]}
                          onChange={(e) => setPasswordForm({ ...passwordForm, [field]: e.target.value })}
                          required
                          placeholder="••••••••"
                          className="w-full px-4 py-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400"
                        />
                      </div>
                    );
                  })}

                  <button type="submit" className="btn-primary w-full">
                    Update Password
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
